import {
  Code,
  FormatBold,
  FormatColorFill,
  FormatItalic,
  FormatListBulleted,
  FormatStrikethrough,
  FormatUnderlined,
  Functions,
  InsertLink,
  MoreHoriz,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Editor } from '@tiptap/react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

interface TextFormattingToolbarProps {
  editor: Editor;
}

export const TextFormattingToolbar = memo(({ editor }: TextFormattingToolbarProps) => {
  const { t } = useTranslation();

  if (!editor) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        p: 1,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
      }}
    >
      <Tooltip title={t('editor.list')}>
        <IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <FormatListBulleted fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('editor.text_color')}>
        <IconButton size="small">
          <FormatColorFill fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={`${t('editor.bold')} (Ctrl+B)`}>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
        >
          <FormatBold fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={`${t('editor.italic')} (Ctrl+I)`}>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
        >
          <FormatItalic fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={`${t('editor.underline')} (Ctrl+U)`}>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          color={editor.isActive('underline') ? 'primary' : 'default'}
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('editor.link')}>
        <IconButton size="small">
          <InsertLink fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('editor.strikethrough')}>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          color={editor.isActive('strike') ? 'primary' : 'default'}
        >
          <FormatStrikethrough fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('editor.code')}>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleCode().run()}
          color={editor.isActive('code') ? 'primary' : 'default'}
        >
          <Code fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('editor.math')}>
        <IconButton size="small">
          <Functions fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton size="small">
        <MoreHoriz fontSize="small" />
      </IconButton>
    </Box>
  );
});

TextFormattingToolbar.displayName = 'TextFormattingToolbar';
